import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Link,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { borderRadius, getSurfaceBorder, getUnifiedScreenFrame } from '../../../theme/theme';
import { pipeexRuntimeConfig } from '../../../shared/config/pipeexRuntimeConfig';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import {
  userErrorsApi,
  type AdminPcfRunDetail,
  type AdminPcfRunListItem,
  type ListRunsQuery,
} from '../api/userErrorsApi';

const DEFAULT_LIMIT = 50;
const MAX_LIMIT = 200;

function defaultFromIsoUtc(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 7);
  return d.toISOString();
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return '-';
  }
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatGrouping(grouping: number): string {
  if (grouping === 0) {
    return 'System name';
  }
  if (grouping === 1) {
    return 'System type';
  }
  return `Mode ${grouping}`;
}

/**
 * Build a Grafana Tempo drill-down URL from a 32-hex trace id. Encoded
 * shape matches Grafana Explore's `?orgId=1&left=<encoded-json>` schema.
 * Returns null when `GRAFANA_BASE_URL` is not configured — the UI then
 * renders the trace id as plain text instead of a link.
 */
function buildGrafanaTraceUrl(traceIdHex: string): string | null {
  const base = pipeexRuntimeConfig.grafanaBaseUrl;
  if (!base) {
    return null;
  }
  const left = {
    datasource: 'Tempo',
    queries: [{ query: traceIdHex }],
  };
  return `${base.replace(/\/$/, '')}/explore?orgId=1&left=${encodeURIComponent(
    JSON.stringify(left)
  )}`;
}


const UserErrorsPage: React.FC = () => {
  const theme = useTheme();
  const screenFrame = getUnifiedScreenFrame(theme);

  const [status, setStatus] = useState<string>('failed');
  const [fromIso, setFromIso] = useState<string>(defaultFromIsoUtc());
  const [toIso, setToIso] = useState<string>('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);

  const [items, setItems] = useState<AdminPcfRunListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [detail, setDetail] = useState<AdminPcfRunDetail | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const query = useMemo<ListRunsQuery>(() => ({
    status: status || undefined,
    from: fromIso || undefined,
    to: toIso || undefined,
    skip: page * rowsPerPage,
    limit: rowsPerPage,
  }), [status, fromIso, toIso, page, rowsPerPage]);

  const fetchData = useCallback(async (request: ListRunsQuery) => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await userErrorsApi.listRuns(request);
      setItems(result.items);
      setTotal(result.total);
    } catch (error: unknown) {
      let message = 'Failed to load PCF run history';
      if (error instanceof Error && error.message.trim() !== '') {
        message = error.message;
      } else if (error && typeof error === 'object' && 'response' in error) {
        const response = error.response;
        if (response && typeof response === 'object' && 'data' in response) {
          const data = response.data;
          if (typeof data === 'string') {
            message = data;
          } else if (data && typeof data === 'object' && 'message' in data
            && typeof data.message === 'string') {
            message = data.message;
          }
        }
      }
      setLoadError(message);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(query);
  }, [fetchData, query]);

  const handleViewDetails = useCallback(async (traceId: string | null) => {
    if (!traceId) {
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      setDetail(await userErrorsApi.getRunDetail(traceId));
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Math.min(
      MAX_LIMIT,
      Math.max(1, parseInt(event.target.value, 10) || DEFAULT_LIMIT)
    );
    setRowsPerPage(value);
    setPage(0);
  };

  return (
    <Box
      sx={{
        height: '100vh',
        backgroundColor: 'background.default',
        color: 'text.primary',
        p: 0,
        overflow: 'hidden',
      }}
    >
      <Paper
        component="main"
        aria-label="User Errors Admin"
        elevation={0}
        sx={{
          ...screenFrame,
          width: '100%',
          height: '100%',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          p: { xs: 1.5, md: 2.5 },
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          User Errors
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          This history contains operational metadata only. Customer models,
          configuration, generated files, and raw errors are never available here.
        </Alert>

        <Box
          component="section"
          aria-label="User error filters"
          data-testid="user-errors-filter-strip"
          sx={{
            mb: 2,
            p: { xs: 1, md: 1.25 },
            borderRadius: `${borderRadius.md}px`,
            border: getSurfaceBorder(theme),
            backgroundColor: alpha(
              theme.palette.background.paper,
              theme.palette.mode === 'light' ? 0.72 : 0.28
            ),
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ flexWrap: 'wrap' }}>
            <TextField
              select
              label="Status"
              value={status}
              size="small"
              onChange={(event) => {
                setPage(0);
                setStatus(event.target.value);
              }}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="partial_success">Partial success</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>
            <TextField
              label="From (UTC ISO)"
              value={fromIso}
              size="small"
              onChange={(event) => {
                setPage(0);
                setFromIso(event.target.value);
              }}
              sx={{ minWidth: 240 }}
            />
            <TextField
              label="To (UTC ISO)"
              value={toIso}
              size="small"
              onChange={(event) => {
                setPage(0);
                setToIso(event.target.value);
              }}
              sx={{ minWidth: 240 }}
            />
          </Stack>
        </Box>

        {loadError && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Started (UTC)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Grouping</TableCell>
                  <TableCell align="right">Elements</TableCell>
                  <TableCell align="right">Files</TableCell>
                  <TableCell align="right">Duration</TableCell>
                  <TableCell>Local cache</TableCell>
                  <TableCell>Trace</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <OperationProgressIndicator size={24} ariaLabel="Loading user errors" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No matching runs.
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.map((row) => {
                  const grafanaUrl = row.trace_id ? buildGrafanaTraceUrl(row.trace_id) : null;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>{formatDate(row.started_at_utc)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status || 'unknown'}
                          color={row.status === 'failed' ? 'error'
                            : row.status === 'success' ? 'success'
                            : 'default'}
                        />
                      </TableCell>
                      <TableCell>{row.error_stage || '-'}</TableCell>
                      <TableCell>{formatGrouping(row.grouping)}</TableCell>
                      <TableCell align="right">{row.element_count}</TableCell>
                      <TableCell align="right">{row.files_produced}</TableCell>
                      <TableCell align="right">{row.duration_ms} ms</TableCell>
                      <TableCell>{row.local_cache_hit ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {row.trace_id ? (
                          grafanaUrl ? (
                            <Link href={grafanaUrl} target="_blank" rel="noopener noreferrer">
                              {row.trace_id.slice(0, 8)}…
                            </Link>
                          ) : (
                            <span>{row.trace_id.slice(0, 8)}…</span>
                          )
                        ) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewDetails(row.trace_id)}
                            disabled={!row.trace_id}
                          >
                            View details
                          </Button>
                          {grafanaUrl && (
                            <Button
                              size="small"
                              variant="outlined"
                              href={grafanaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Open in Grafana
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100, 200]}
        />

        <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Run detail</DialogTitle>
          <DialogContent dividers>
            {detailLoading && <OperationProgressIndicator size={24} ariaLabel="Loading run detail" />}
            {!detailLoading && detail && (
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="subtitle2">Run ID</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{detail.id}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Trace ID</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {detail.trace_id || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Status / stage</Typography>
                  <Typography variant="body2">
                    {detail.status} / {detail.error_stage || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Timing</Typography>
                  <Typography variant="body2">
                    Created {formatDate(detail.created_at_utc)}; started {formatDate(detail.started_at_utc)};
                    finished {formatDate(detail.finished_at_utc)}; {detail.duration_ms} ms
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Calculation metadata</Typography>
                  <Typography variant="body2">
                    {formatGrouping(detail.grouping)}; {detail.element_count} elements;{' '}
                    {detail.files_produced} files; local cache {detail.local_cache_hit ? 'hit' : 'miss'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Versions</Typography>
                  <Typography variant="body2">
                    Plugin {detail.plugin_version || '-'}; pipeline {detail.pipeline_version || '-'};
                    build {detail.git_sha || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Retention</Typography>
                  <Typography variant="body2">{formatDate(detail.retention_clear_at)}</Typography>
                </Box>
              </Stack>
            )}
            {!detailLoading && !detail && (
              <Alert severity="error">Failed to load detail.</Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default UserErrorsPage;
